// Globals
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Models
import { ChannelType } from '@/models/channel.model';

// Modules
import { Header } from '@/modules/Layout';
import {
  LLMTemplateContentForm,
  LLMTemplateVariablesList,
  TLLMTemplateFormValues,
  useGetLLMTemplateVariables,
} from '@/modules/LLMTemplateCommon';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { selectLLMTemplateDetailsIsSaving, updateBaseTemplate } from '../../feature';

// Components
import { BackLink, showSuccessToast, useForm } from '@/components';
import { CheckIcon } from '@/icons';
import { Button, Spinner } from '@/ui';
import {
  StyledActions,
  StyledContainer,
  StyledPageWrapper,
  StyledSpinnerWrapper,
  StyledVariablesSidebar,
} from './LLMTemplateDetailsContainerStyles';

// Helpers
import { ROUTES } from '@/constants/routes';
import { useGetBaseTemplateByType } from '../../hooks';
import { getLLMTemplateDetailsInitialValues } from '../../helpers';

const LLMTemplateDetailsContainer = () => {
  const { t } = useTranslation();
  const { channelType } = useParams<{ channelType: ChannelType }>();
  const dispatch = useAppDispatch();
  const { isLoading, template } = useGetBaseTemplateByType({ channelType });
  const { isLoading: isVariablesLoading, variables } = useGetLLMTemplateVariables();
  const isSaving = useAppState(selectLLMTemplateDetailsIsSaving);
  const [form] = useForm();

  const initialValues = useMemo(() => getLLMTemplateDetailsInitialValues(template), [template]);

  useEffect(() => {
    if (template) {
      form.setFieldsValue(getLLMTemplateDetailsInitialValues(template));
    }
  }, [template, form]);

  const handleSave = async (values: TLLMTemplateFormValues) => {
    if (!channelType) return;

    try {
      await dispatch(updateBaseTemplate({ channelType, content: values.content })).unwrap();
      showSuccessToast({ message: t('save_changes') });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveClick = () => {
    form.submit();
  };

  return (
    <StyledPageWrapper>
      <Header
        suptitle={<BackLink to={ROUTES.LLM_TEMPLATES}>{t('back_to_llm_templates')}</BackLink>}
        title={t('llm_template_details_title', { type: channelType })}
      >
        <StyledActions>
          <Button
            icon={<CheckIcon />}
            type="primary"
            loading={isSaving}
            onClick={handleSaveClick}
            disabled={!channelType || isLoading}
          >
            {t('btn_save')}
          </Button>
        </StyledActions>
      </Header>
      <StyledSpinnerWrapper>
        <Spinner size="large" spinning={isLoading}>
          <StyledContainer>
            <LLMTemplateContentForm form={form} initialValues={initialValues} onSubmit={handleSave} />
            <StyledVariablesSidebar>
              <LLMTemplateVariablesList variables={variables} isLoading={isVariablesLoading} />
            </StyledVariablesSidebar>
          </StyledContainer>
        </Spinner>
      </StyledSpinnerWrapper>
    </StyledPageWrapper>
  );
};

export default LLMTemplateDetailsContainer;
